import React, { useRef, useState, useLayoutEffect } from 'react';
import { CustomEventTypes } from '../../globals';
import useEventListener from '../reactUtils/useEventListener';

type ChildProps = {
	key?: React.Key;
	onShutdown?: () => void;
	injectionRef?: { current: { linkedShutdown: () => void } };
};

interface ModalProps {
	id: string;
	contentRect?: {
		x?: number;
		y?: number;
		width?: number;
		height?: number;
	};
	onShutdown?: () => void;
	allowClickOut?: boolean;
	showBackdrop?: boolean;
	clickThrough?: boolean;
	clickThroughCover?: boolean;
	dataName?: string;
	onModalCloseCallback?: () => void;
	children?: React.ReactNode;
}

export default function Modal(props: ModalProps)
{
	useEventListener(CustomEventTypes.destroyModalRequested, (e) =>
	{
		if (props.id === e.detail.id) shutDown();
	}, document);

	if ( ! verifyProps(props) )
	{
		return;
	}

	const coverRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [contentStyle, setContentStyle] = useState({ left: NaN, top: NaN, width: NaN, height: NaN });

	useLayoutEffect(() =>
	{
		const defaultStyle = { left: NaN, top: NaN, width: NaN, height: NaN };

		if (props.contentRect)
		{
			defaultStyle.left = props.contentRect.x ?? NaN;
			defaultStyle.top = props.contentRect.y ?? NaN;
			defaultStyle.width = props.contentRect.width ?? NaN;
			defaultStyle.height = props.contentRect.height ?? NaN;
		}

		const adjustPosition = () =>
		{
			if(!contentRef.current) return;

			// make sure the modal is within the window
			if(!isNaN(defaultStyle.left))
			{
				defaultStyle.left = Math.max(0, defaultStyle.left);

				if(defaultStyle.left + contentRef.current.offsetWidth > window.innerWidth)
				{
					defaultStyle.left = window.innerWidth - contentRef.current.offsetWidth;
				}
			}

			if(!isNaN(defaultStyle.top))
			{
				defaultStyle.top = Math.max(0, defaultStyle.top);

				if(defaultStyle.top + contentRef.current.offsetHeight > window.innerHeight)
				{
					defaultStyle.top = window.innerHeight - contentRef.current.offsetHeight;
				}
			}
		};

		adjustPosition();
		setContentStyle(defaultStyle);
	}, [props.contentRect, contentRef.current]);

	function shutDown()
	{
		props.onShutdown?.();
		props.onModalCloseCallback?.();
	}

	function verifyProps(props: ModalProps): boolean
	{
		if ( props.clickThrough && props.allowClickOut)
		{
			console.error("ClickThrough and AllowClickout options are incompatible")
			return false;
		}
		if ( props.clickThrough && props.showBackdrop )
		{
			console.error("ClickThrough and ShowBackdrop options are incompatible")
			return false;
		}
		return true;
	}

	const modalContent = (
		<>
			{props.showBackdrop && <div className="backdrop" />}
			<div
				className={"modalContent"}
				ref={contentRef}
				style={{
					left: `${contentStyle.left}px`,
					top: `${contentStyle.top}px`,
					width: `${contentStyle.width}px`,
					height: `${contentStyle.height}px`,
					pointerEvents: "auto",
				}}
				onClick={(e) => e.stopPropagation()}
				onContextMenu={(e) =>
				{
					e.preventDefault();
					e.stopPropagation();
				}}
			>
				{React.Children.map(props.children, (child, idx) =>
				{
					if (!React.isValidElement<ChildProps>(child)) return child;
					const fnShutdown = () =>
					{
						child.props.onShutdown?.();
						shutDown();
					};
					if ( child.props.injectionRef?.current )
					{
						child.props.injectionRef.current.linkedShutdown = fnShutdown;
					}
					return React.cloneElement(child, { key: idx, onShutdown: fnShutdown });
				})}
			</div>
		</>
	);

	if (props.clickThrough && !props.allowClickOut)
	{
		return modalContent;
	}
	else if ( props.clickThrough && props.allowClickOut)
	{
		console.error("ClickThrough and AllowClickout options are incompatible")
	}
	else
	{
		// Wrap it in the screenCover components
		return (
			<div
				className={`modalRoot screenCover ${props.clickThroughCover ? "clickThroughCover" : ""}`}
				data-name={props.dataName ?? "modal-root"}
				ref={coverRef}
				onClick={(e) =>
				{
					e.stopPropagation();
					if (props.allowClickOut) shutDown();
				}}
				onContextMenu={(e) =>
				{
					e.preventDefault();
					if (props.allowClickOut) shutDown();
				}}
			>
				{modalContent}
			</div>
		);
	}
}
